import { useState } from 'react';
import { useNavigate, Navigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../services/api';
import { ResetPasswordModal } from '../components/ResetPasswordModal';
import './LoginPage.css';

export const LoginPage = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);

  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectUrl = searchParams.get('redirect');

  // If already authenticated, redirect to intended destination or dashboard
  if (isAuthenticated) {
    if (redirectUrl) {
      window.location.href = redirectUrl;
      return null;
    }
    return <Navigate to="/dashboard" replace />;
  }

  const handleForgotPassword = async (e: React.MouseEvent) => {
    e.preventDefault();
    setError(null);

    if (!username.trim()) {
      setError('Please enter your username before requesting a password reset.');
      return;
    }

    try {
      await api.forgotPassword(username);
      setShowResetModal(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Request failed. Please try again.');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      await login(username, password);
      if (redirectUrl) {
        window.location.href = redirectUrl;
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-card">
          <div className="login-header">
            <h1>QBD Connect</h1>
            <p className="login-subtitle">Sign in to your account</p>
          </div>

          {error && (
            <div className="alert alert-error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="login-form">
            <div className="form-group">
              <label htmlFor="username">Username</label>
              <input
                type="text"
                id="username"
                className="form-input"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                autoComplete="username"
                placeholder="Enter your username"
                disabled={isLoading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="password">Password</label>
              <input
                type="password"
                id="password"
                className="form-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                placeholder="Enter your password"
                disabled={isLoading}
              />
            </div>

            <button
              type="submit"
              className="btn btn-primary btn-block"
              disabled={isLoading}
            >
              {isLoading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>
          <div>
            <br/>
            <Link to={redirectUrl ? `/signUp?redirect=${encodeURIComponent(redirectUrl)}` : '/signUp'} className="back-link">Need an account?</Link>&nbsp;&nbsp;
            <button type="button" className="back-link forgot-link-btn" onClick={handleForgotPassword}>Forgot your password?</button>
          </div>
        </div>
      </div>
      <ResetPasswordModal
        isOpen={showResetModal}
        username={username}
        onClose={() => setShowResetModal(false)}
      />
    </div>
  );
};
